// The studio: write something, and save the bits worth keeping.
//
// It does one thing Compose does not, and does not do the one thing Compose
// does. It never publishes, never touches a connection and never creates a
// post. It writes rows into `content_drafts`, and Compose reads them
// (docs/AI_STUDIO_PLAN.md §2). That is the whole relationship, and it is why
// this folder can be deleted without Compose noticing.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { currentWorkspace } from "../lib/api";
import { useWorkspace } from "../lib/WorkspaceContext";
import {
  can, isPaywall, limitOf, loadEntitlements, paywallMessage, remaining,
} from "../lib/entitlements";
import { logError, newRunId } from "../lib/logger";
import { Loading } from "../components/Spinner";
import { Composer } from "./Composer";
import { Turn } from "./Turn";
import { Skills } from "./Skills";
import { ApiKey } from "./ApiKey";
import { deleteThread, generate, listKeyStatusSafe, listThreads, listTurns, loadPricing } from "./api";
import type {
  AiCreditCost, AiGeneration, AiModel, AiThread, DraftIntent, Entitlements,
} from "../lib/types";

type Tab = "write" | "knowledge" | "key";

export function Studio() {
  const { can: canDo } = useWorkspace();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [tab, setTab] = useState<Tab>("write");

  const [models, setModels] = useState<AiModel[]>([]);
  const [costs, setCosts] = useState<AiCreditCost[]>([]);
  const [modelKey, setModelKey] = useState<string | null>(null);

  const [threads, setThreads] = useState<AiThread[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<AiGeneration[] | null>(null);

  const [intent, setIntent] = useState<DraftIntent>("post");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Answered by the last generation, which is more current than the entitlement
  // snapshot this screen loaded when it opened.
  const [creditsLeft, setCreditsLeft] = useState<number | null | undefined>(undefined);
  // A workspace on its own key is not metered. Without this the composer would
  // refuse to write anything once the plan's credits ran out, for a workspace
  // whose drafts have not touched them in weeks.
  const [byokActive, setByokActive] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const mayGenerate = canDo("content.ai_generate");
  const mayManageBilling = canDo("billing.manage");
  const planHasAi = can(ent, "ai_description");
  const planHasByok = can(ent, "ai_byok");
  const mayUsePremium = can(ent, "ai_premium_model");

  useEffect(() => {
    currentWorkspace()
      .then(async (ws) => {
        setWorkspaceId(ws);
        // A failed entitlement read must never block the screen: the database
        // decides regardless, and null here only means no hints.
        setEnt(await loadEntitlements(ws).catch(() => null));
        setThreads(await listThreads(ws).catch(() => []));
        setByokActive(await listKeyStatusSafe(ws));
      })
      .catch((e) => setError(String(e.message ?? e)));

    loadPricing()
      .then(({ models, costs }) => { setModels(models); setCosts(costs); })
      .catch(() => { /* the composer simply shows no prices */ });
  }, []);

  // Which models this plan may pick between. The server decides again; this
  // only keeps the dropdown from offering something that will quietly downgrade.
  const available = useMemo(
    () => models.filter((m) => mayUsePremium || !m.is_premium),
    [models, mayUsePremium],
  );

  useEffect(() => {
    if (available.length === 0) return;
    if (!modelKey || !available.some((m) => m.key === modelKey)) {
      // The best one the plan allows: rows are ordered cheapest first.
      setModelKey(available[available.length - 1].key);
    }
  }, [available, modelKey]);

  // The credit figure. Entitlements when the screen opened; the generation
  // response after that, because it is read after the charge.
  const planCredits = remaining(ent, "ai_credits_per_month");
  // null means "not metered", which is what unlimited and BYOK both are.
  const credits = byokActive
    ? null
    : creditsLeft === undefined ? planCredits : creditsLeft;
  const monthlyLimit = limitOf(ent, "ai_credits_per_month");

  const openThread = useCallback(async (id: string | null) => {
    setThreadId(id);
    setError(null);
    setPaywall(null);
    if (!id) { setTurns([]); return; }
    setTurns(null);
    setTurns(await listTurns(id).catch(() => []));
  }, []);

  /**
   * Throw the conversation away. The generations go with it — they are history
   * of a thing the user is saying they do not want — but anything saved from it
   * stays: `content_drafts.generation_id` is nullable precisely so that deleting
   * the chat can never take the caption out of Compose.
   */
  async function discard() {
    if (!threadId || !workspaceId) return;
    setDiscarding(true);
    try {
      await deleteThread(threadId);
      setThreads(await listThreads(workspaceId).catch(() => []));
      await openThread(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDiscarding(false);
    }
  }

  async function run(text: string) {
    if (!workspaceId) return;
    const runId = newRunId();
    setBusy(true);
    setError(null);
    setPaywall(null);
    setNotice(null);

    try {
      const result = await generate({
        workspaceId,
        threadId,
        intent,
        brief: text,
        modelKey,
      });

      setThreadId(result.threadId);
      setTurns(await listTurns(result.threadId).catch(() => turns));
      setThreads(await listThreads(workspaceId).catch(() => threads));
      setBrief("");
      // Unchanged when the workspace is on its own key, which is exactly what
      // "creditsCharged: false" means.
      if (result.creditsCharged) setCreditsLeft(result.creditsLeft);

      const notes: string[] = [];
      if (result.truncated) {
        notes.push("It ran out of room, so the ending is abrupt — ask for something shorter.");
      }
      if (result.knowledgeOmitted.length > 0) {
        // Never silently: a file that did not fit changes the output, and a
        // customer who cannot see that has no way to diagnose it.
        notes.push(
          `Too much brand knowledge to send at once — ${result.knowledgeOmitted.join(", ")} ` +
          `${result.knowledgeOmitted.length === 1 ? "was" : "were"} left out. ` +
          `Turn something off to choose what goes in.`,
        );
      }
      setNotice(notes.length > 0 ? notes.join(" ") : null);
    } catch (e) {
      if (isPaywall(e)) {
        setPaywall(paywallMessage(e));
      } else {
        setError((e as Error).message);
        logError("ai_generate", e, { step: "generate", workspaceId, runId });
      }
    } finally {
      setBusy(false);
    }
  }

  if (!workspaceId && !error) return <div className="wrap muted">Loading…</div>;

  // The plan says no. Said once, at the top, rather than by every disabled
  // control on the screen.
  if (workspaceId && ent && !planHasAi) {
    return (
      <>
        <h1>Studio</h1>
        <div className="card">
          <div className="notice upgrade">
            Writing with AI is not included in your plan. <Link to="/plans">See plans</Link>
          </div>
          <p className="muted">
            The studio drafts captions, short descriptions and punch lines in your own voice, and
            saves them where the Compose screen can pick them up.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="studio-head">
        <h1>Studio</h1>
        <span className="spacer" />
        {byokActive ? (
          <span className="chip ok" title="Drafts are billed to your own provider account">
            On your own key
          </span>
        ) : credits !== null && monthlyLimit !== null ? (
          <span className={`chip${credits <= 10 ? " warn" : " ok"}`}
                title="Credits left this month">
            {credits} of {monthlyLimit} credits
          </span>
        ) : null}
      </div>

      <div className="row studio-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "write"}
                className={tab === "write" ? "primary" : ""}
                onClick={() => setTab("write")}>Write</button>
        <button role="tab" aria-selected={tab === "knowledge"}
                className={tab === "knowledge" ? "primary" : ""}
                onClick={() => setTab("knowledge")}>Brand knowledge</button>
        <button role="tab" aria-selected={tab === "key"}
                className={tab === "key" ? "primary" : ""}
                onClick={() => setTab("key")}>Your AI key</button>
      </div>

      {error && <div className="card err">{error}</div>}

      {tab === "knowledge" && workspaceId && (
        <Skills workspaceId={workspaceId} canEdit={mayGenerate} />
      )}

      {tab === "key" && workspaceId && (
        <ApiKey workspaceId={workspaceId} allowed={planHasByok} canManage={mayManageBilling} />
      )}

      {tab === "write" && (
        <>
          {!mayGenerate && (
            <div className="card">
              <div className="notice">
                Your role can prepare posts but not draft with AI — ask a manager or an owner in
                your workspace.
              </div>
            </div>
          )}

          {threads.length > 0 && (
            <div className="card studio-threads">
              <label className="visually-hidden" htmlFor="studio-thread">Conversation</label>
              <select id="studio-thread" value={threadId ?? ""}
                      onChange={(e) => openThread(e.target.value || null)}>
                <option value="">New conversation</option>
                {threads.map((t) => (
                  <option key={t.id} value={t.id}>{t.title ?? "Untitled"}</option>
                ))}
              </select>
              {threadId && (
                <>
                  <button onClick={() => openThread(null)}>Start a new one</button>
                  <button className="danger" onClick={discard} disabled={discarding}>
                    {discarding ? "Discarding…" : "Discard"}
                  </button>
                </>
              )}
            </div>
          )}

          {threadId && turns === null && <Loading label="Loading that conversation…" />}

          {(turns ?? []).map((t) => (
            <Turn key={t.id} turn={t} workspaceId={workspaceId!}
                  canSave={canDo("post.create")}
                  onRegenerate={(text) => run(text)} />
          ))}

          {notice && <div className="card"><div className="notice">{notice}</div></div>}
          {paywall && (
            <div className="card">
              <div className="notice upgrade">{paywall} <Link to="/plans">See plans</Link></div>
            </div>
          )}

          <Composer
            intent={intent}
            onIntent={setIntent}
            brief={brief}
            onBrief={setBrief}
            models={available}
            costs={costs}
            modelKey={modelKey}
            onModel={setModelKey}
            busy={busy}
            disabled={!mayGenerate}
            onGenerate={() => run(brief)}
            credits={credits}
          />

          <p className="muted studio-foot">
            Nothing here publishes. Save what you like, then pick it up in{" "}
            <Link to="/compose">Compose</Link> under “Use a saved draft”.
          </p>
        </>
      )}
    </>
  );
}
